import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  ResponseListLocationUsers,
  ResponseListMemberships,
  useAddExistingUserToLocation,
  useListLocationUsers,
  useListOrganisationMembers,
} from '../../../../../../../app/ApiGen';
import { UserAvatar } from '../../../../../../../components/Avatar/User';
import { DataErrorHandler } from '../../../../../../../components/ErrorHandler';
import { errorSnackbar } from '../../../../../../../components/ErrorSnackbar';
import { SelectableItem, SelectionList } from '../../../../../../../components/SelectionList';
import { FormActions, Loading } from '../../../../../../../design-system';
import type { SubState } from '../../../../../../../store/types';
import { getSelectableUsers } from '../../../../../../../utils/users/form';

interface SelectUserContext {
  location: SubState<'location'>;
  organisation: SubState<'organisation'>;
  organisationMembers: ResponseListMemberships;
  usersInLocation: ResponseListLocationUsers;
}

const SelectUser: React.FC<SelectUserContext> = ({ location, organisation, organisationMembers, usersInLocation }) => {
  const [selectedUserId, setSelectedUserId] = React.useState<string>();
  const [submitting, setSubmitting] = React.useState(false);
  const { push } = useHistory();
  const addLocationUser = useAddExistingUserToLocation({ locationId: location.id });
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const selectableUsers: SelectableItem[] = getSelectableUsers(organisationMembers.included, usersInLocation.data);

  const onUserSelect = React.useCallback((user: SelectableItem) => setSelectedUserId(user.id), [setSelectedUserId]);

  const onAddUser = React.useCallback(() => {
    if (!selectedUserId) {
      return;
    }
    setSubmitting(true);
    addLocationUser
      .mutate({ data: { type: 'users', id: selectedUserId } })
      .then(() => {
        enqueueSnackbar('User added to location', { variant: 'success' });
        push(`/organisations/${organisation.slug}/locations/${location.id}/users`);
      })
      .catch((e) => {
        setSubmitting(false);
        errorSnackbar('Unable to add user to location', closeSnackbar, enqueueSnackbar, e);
      });
  }, [addLocationUser, closeSnackbar, enqueueSnackbar, location.id, organisation.slug, push, selectedUserId]);

  return (
    <SelectionList
      getImage={({ title, color, image }) => <UserAvatar embedded user={{ name: title, color, avatar: image }} />}
      items={selectableUsers}
      label="user"
      onSelectItem={onUserSelect}
      lowerActions={
        <FormActions>
          <Button color="primary" disabled={submitting || !selectedUserId} onClick={onAddUser} variant="contained">
            {submitting ? <CircularProgress size={24} color="inherit" /> : 'Add user'}
          </Button>
        </FormActions>
      }
    />
  );
};

export const SelectUserStep: React.FC<{ location: SubState<'location'>; organisation: SubState<'organisation'> }> = ({
  location,
  organisation,
}) => {
  const organisationMembers = useListOrganisationMembers({ organisationId: organisation.id });
  const usersInLocation = useListLocationUsers({ locationId: location.id });

  if (organisationMembers.loading || usersInLocation.loading) {
    return <Loading />;
  }

  if (!usersInLocation.data || !organisationMembers.data || usersInLocation.error || organisationMembers.error) {
    return (
      <>
        {(usersInLocation.error || !usersInLocation.data) && (
          <DataErrorHandler
            description="Unable to load location users"
            error={usersInLocation.error}
            refetch={usersInLocation.refetch}
            type="embedded"
          />
        )}
        {(organisationMembers.error || !organisationMembers.data) && (
          <DataErrorHandler
            description="Unable to load organisation users"
            error={organisationMembers.error}
            refetch={organisationMembers.refetch}
            type="embedded"
          />
        )}
      </>
    );
  }

  return (
    <SelectUser
      location={location}
      organisation={organisation}
      organisationMembers={organisationMembers.data}
      usersInLocation={usersInLocation.data}
    />
  );
};
